import React, { useState } from 'react';
import { FileDown, BarChart3 } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { useBookingReport, useExportBookingReport } from '../../hooks/useBookingReports';
import BookingDashboardCard from '../../components/booking/BookingDashboardCard';
import BookingStatsCard from '../../components/booking/BookingStatsCard';
import Spinner from '../../components/Spinner';

const STATUS_COLORS: Record<string, string> = {
  Pending: '#f59e0b',
  Confirmed: '#002144',
  Completed: '#10b981',
  Cancelled: '#801414',
  'No Show': '#64748B',
};

export default function BookingReportsPage() {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const { data: report, isLoading } = useBookingReport({ dateFrom: dateFrom || undefined, dateTo: dateTo || undefined });
  const { exportCsv } = useExportBookingReport();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-strivre-navy flex items-center gap-2"><BarChart3 size={22} /> Booking Reports</h1>
          <p className="text-sm text-strivre-mutedtext mt-1">Analyze booking volume, revenue, and service performance.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => exportCsv({ dateFrom, dateTo })} className="btn-outline btn-sm flex items-center gap-1.5"><FileDown size={14} /> CSV</button>
        </div>
      </div>

      <div className="bg-white border border-strivre-lightnavy rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
        <div>
          <label className="block text-xs font-semibold text-strivre-mutedtext mb-1">Date From</label>
          <input type="date" className="input-base h-10" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-strivre-mutedtext mb-1">Date To</label>
          <input type="date" className="input-base h-10" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Spinner size="h-8 w-8" />
          <p className="mt-3 text-strivre-mutedtext">Loading report...</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <BookingStatsCard title="Total Bookings" value={report?.totalBookings ?? 0} icon={<BarChart3 size={20} />} />
            <BookingStatsCard title="Completed" value={report?.completedBookings ?? 0} icon={<BarChart3 size={20} />} />
            <BookingStatsCard title="Cancelled" value={report?.cancelledBookings ?? 0} icon={<BarChart3 size={20} />} highlight />
            <BookingStatsCard title="Revenue" value={`$${(report?.totalRevenue ?? 0).toLocaleString()}`} icon={<BarChart3 size={20} />} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <BookingDashboardCard title="Bookings By Service" className="lg:col-span-1">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={report?.bookingsByService ?? []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8EEF4" />
                  <XAxis dataKey="serviceName" tick={{ fontSize: 10, fill: '#64748B' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748B' }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#002144" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </BookingDashboardCard>

            <BookingDashboardCard title="Bookings By Month" className="lg:col-span-1">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={report?.bookingsByMonth ?? []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8EEF4" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748B' }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#801414" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </BookingDashboardCard>

            <BookingDashboardCard title="Bookings By Status" className="lg:col-span-1">
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={report?.bookingsByStatus ?? []} dataKey="count" nameKey="status" outerRadius={80}>
                    {(report?.bookingsByStatus ?? []).map((entry) => (
                      <Cell key={entry.status} fill={STATUS_COLORS[entry.status] ?? '#64748B'} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </BookingDashboardCard>
          </div>
        </>
      )}
    </div>
  );
}
